import React, { useCallback, useState, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ScoreWidgetFieldForm } from '@traquility/ui/diary';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '../ui/common/Dropdown';
import type {
  ScoreWidgetFieldFormData,
  ScoreWidgetFieldFormContainerProps,
  ScoreWidgetFieldFormLabels,
} from '../shared/types/scoreWidgetFieldForm';

export type { ScoreWidgetFieldFormContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10,
    },
  },
});

const CREATE_MUTATION = `
  mutation CreateScoreWidgetField($input: CreateScoreWidgetFieldInput!) {
    createScoreWidgetField(input: $input) {
      numericField {
        id
        name
      }
      errors
    }
  }
`;

const UPDATE_MUTATION = `
  mutation UpdateScoreWidgetField($input: UpdateScoreWidgetFieldInput!) {
    updateScoreWidgetField(input: $input) {
      numericField {
        id
        name
      }
      errors
    }
  }
`;

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  labels: ScoreWidgetFieldFormLabels;
  isDeleting: boolean;
}

function DeleteModal({ isOpen, onClose, onDelete, labels: l, isDeleting }: DeleteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{l.deleteTitle}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isDeleting}></button>
          </div>
          <div className="modal-body">
            <p>{l.deleteConfirmation}</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isDeleting}>
              {l.cancel}
            </button>
            <button type="button" className="btn btn-danger" onClick={onDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.deleteButton}
                </>
              ) : (
                l.deleteButton
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreWidgetFieldFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  scoreWidgetId,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
  mode,
  fieldTypeOptions,
  unitOptions,
}: ScoreWidgetFieldFormContainerProps) {
  const [formData, setFormData] = useState<ScoreWidgetFieldFormData>(initialData);
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Listen for icon picker selection
  useEffect(() => {
    const handleIconSelected = () => {
      if ((window as unknown as { __iconPickerIconWasSelected?: boolean }).__iconPickerIconWasSelected) {
        const iconInput = document.querySelector('[data-icon-picker-target="selectedIcon"]') as HTMLInputElement;
        const scoreInput = document.querySelector('[data-icon-picker-target="scoreInput"]') as HTMLInputElement;
        if (iconInput) {
          setFormData(prev => ({
            ...prev,
            icon: iconInput.value,
            score: scoreInput ? parseInt(scoreInput.value) || prev.score : prev.score,
          }));
        }
        (window as unknown as { __iconPickerIconWasSelected?: boolean }).__iconPickerIconWasSelected = false;
      }
    };

    const iconModal = document.getElementById('iconModal');
    iconModal?.addEventListener('hidden.bs.modal', handleIconSelected);
    return () => iconModal?.removeEventListener('hidden.bs.modal', handleIconSelected);
  }, []);

  const handleSubmit = useCallback(async (data: ScoreWidgetFieldFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const isEdit = mode === 'edit';
    const mutation = isEdit ? UPDATE_MUTATION : CREATE_MUTATION;

    const input: Record<string, unknown> = {
      scoreWidgetId,
      name: data.name,
      icon: data.icon,
      score: data.score,
      fieldType: data.fieldType,
      units: data.units,
      minValue: data.minValue,
      maxValue: data.maxValue,
      stepValue: data.stepValue,
      targetValue: data.targetValue,
    };

    if (isEdit) {
      input.id = data.id;
    }

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: mutation, variables: { input } }),
      });

      if (!response.ok) throw new Error('Network error');

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateScoreWidgetField : result.data.createScoreWidgetField;

      if (mutationResult.errors?.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      window.location.href = successUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, scoreWidgetId, successUrl, mode]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleIconClick = useCallback(() => {
    // Set up hidden inputs for icon picker
    const container = document.getElementById('react-score-widget-field-form');
    if (!container) return;

    let iconInput = container.querySelector('[data-icon-picker-target="selectedIcon"]') as HTMLInputElement;
    let scoreInput = container.querySelector('[data-icon-picker-target="scoreInput"]') as HTMLInputElement;

    if (!iconInput) {
      iconInput = document.createElement('input');
      iconInput.type = 'hidden';
      iconInput.dataset.iconPickerTarget = 'selectedIcon';
      container.appendChild(iconInput);
    }
    if (!scoreInput) {
      scoreInput = document.createElement('input');
      scoreInput.type = 'hidden';
      scoreInput.dataset.iconPickerTarget = 'scoreInput';
      container.appendChild(scoreInput);
    }

    iconInput.value = formData.icon;
    scoreInput.value = String(formData.score);

    // Mark this container as active for the icon picker
    container.classList.add('icon-picker', 'js-icon-picker-active');

    // Open the modal via custom event (handled by modal controller)
    const iconModal = document.getElementById('iconModal');
    if (iconModal) {
      iconModal.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
    }
  }, [formData.icon, formData.score]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation DeleteScoreWidgetField($input: DeleteScoreWidgetFieldInput!) {
              deleteScoreWidgetField(input: $input) {
                success
                errors
              }
            }
          `,
          variables: { input: { scoreWidgetId, id: initialData.id } },
        }),
      });

      const result = await response.json();

      if (result.errors || result.data.deleteScoreWidgetField.errors?.length > 0) {
        const msgs = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.deleteScoreWidgetField.errors;
        setErrors({ base: msgs });
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsDeleting(false);
    }
  }, [graphqlEndpoint, csrfToken, scoreWidgetId, initialData.id, backUrl]);

  // Update local state when form submits
  const handleFormSubmit = useCallback((data: ScoreWidgetFieldFormData) => {
    setFormData(data);
    handleSubmit(data);
  }, [handleSubmit]);

  return (
    <>
      {mode === 'edit' && (
        <div className="position-absolute" style={{ top: 10, right: 10 }}>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-secondary">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              <DropdownItem className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {labels.delete}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      <ScoreWidgetFieldForm
        initialData={formData}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        onIconClick={handleIconClick}
        errors={errors}
        labels={labels}
        mode={mode}
        fieldTypeOptions={fieldTypeOptions}
        unitOptions={unitOptions}
        isSubmitting={isSubmitting}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        labels={labels}
        isDeleting={isDeleting}
      />
    </>
  );
}

function ScoreWidgetFieldFormContainer(props: ScoreWidgetFieldFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ScoreWidgetFieldFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

let formRoot: Root | null = null;

export function mountScoreWidgetFieldForm(
  container: HTMLElement,
  props: ScoreWidgetFieldFormContainerProps
): Root {
  if (formRoot) formRoot.unmount();
  const root = createRoot(container);
  formRoot = root;
  root.render(<ScoreWidgetFieldFormContainer {...props} />);
  return root;
}

export function unmountScoreWidgetFieldForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default ScoreWidgetFieldFormContainer;
